import React, { FC, ReactNode, useEffect, useState } from 'react'
import { Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material'
import format from 'date-fns/format'
import { useContractorListRequest } from 'src/hooks/api'
import { useDebouncedValue } from 'src/hooks/ui'
import { getContractorTypeLabel, TransferContractor } from './transferWizardHelpers'

/** Back and Next under every step. */
export const WizardFooter: FC<{
  onBack?: () => void
  onNext?: () => void
  nextLabel?: string
  nextDisabled?: boolean
  nextColor?: 'primary' | 'error'
  hint?: ReactNode
}> = ({ onBack, onNext, nextLabel = 'Next', nextDisabled = false, nextColor = 'primary', hint }) => (
  <Grid container spacing={2} alignItems="center" justifyContent="space-between" mt={2}>
    <Grid item xs={12} sm>
      {hint && (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Grid>
    <Grid item xs={12} sm="auto">
      <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}>
        {onBack && (
          <Button variant="outlined" size="large" onClick={onBack}>
            Back
          </Button>
        )}
        {onNext && (
          <Button variant="contained" size="large" color={nextColor} disabled={nextDisabled} onClick={onNext}>
            {nextLabel}
          </Button>
        )}
      </Stack>
    </Grid>
  </Grid>
)

/** Local paging and a debounced search for a grid inside the wizard; the URL stays untouched. */
export const useWizardGrid = (defaultPageSize = 25) => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim())

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch])

  return {
    page,
    pageSize,
    search,
    setSearch,
    debouncedSearch,
    paginationModel: { page, pageSize },
    onPaginationModelChange: (model: { page: number; pageSize: number }) => {
      setPage(model.page)
      setPageSize(model.pageSize)
    },
    requestParams: { limit: pageSize, offset: page * pageSize, ...(debouncedSearch ? { search: debouncedSearch } : {}) }
  }
}

/** Keeps the last known row count while a page loads, so the grid footer does not jump to zero. */
export const useStableCount = (count?: number): number => {
  const [stable, setStable] = useState(count || 0)

  useEffect(() => {
    if (count !== undefined) setStable(count)
  }, [count])

  return stable
}

interface ContractorPickerProps {
  label: string
  value: TransferContractor | null
  onChange: (contractor: TransferContractor | null) => void
  getOptionDisabledReason?: (contractor: TransferContractor) => string | undefined
}

/** Any contractor, HQ or not, found by name on the server (at least 3 characters). */
export const ContractorPicker: FC<ContractorPickerProps> = ({ label, value, onChange, getOptionDisabledReason }) => {
  const [input, setInput] = useState('')
  const search = useDebouncedValue(input.trim())
  const { data, isFetching } = useContractorListRequest({
    params: { search, limit: 20, offset: 0, sort: 'name:asc' },
    options: { enabled: search.length >= 3, keepPreviousData: true }
  })
  const options = (search.length >= 3 ? data?.rows || [] : []) as TransferContractor[]

  return (
    <Autocomplete<TransferContractor>
      value={value}
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionDisabled={(option) => Boolean(getOptionDisabledReason?.(option))}
      loading={isFetching}
      onInputChange={(_, text) => setInput(text)}
      onChange={(_, contractor) => onChange(contractor)}
      noOptionsText={search.length >= 3 ? 'No contractors found' : 'Type at least 3 characters'}
      renderOption={(props, option) => {
        const reason = getOptionDisabledReason?.(option)
        return (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[getContractorTypeLabel(option), reason].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  )
}

const formatDay = (value?: string | null): string => (value ? format(new Date(value), 'PP') : '-')

const activationLabel = (contractor: TransferContractor): { label: string; color: 'success' | 'error' | 'default' } => {
  if (contractor.isActivated === true) return { label: 'Activated', color: 'success' }
  if (contractor.isActivated === false) return { label: 'Deactivated', color: 'error' }
  return { label: 'Not activated', color: 'default' }
}

/** Type, activation, trial and paid plan of a picked contractor. */
export const ContractorSummaryCard: FC<{ title: string; contractor: TransferContractor }> = ({ title, contractor }) => {
  const activation = activationLabel(contractor)
  const trial = contractor.isActiveTrial ? `active, ends ${formatDay(contractor.trialEndedAt)}` : contractor.trialEndedAt ? `ended ${formatDay(contractor.trialEndedAt)}` : 'none'

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6" gutterBottom>
          {contractor.name}
        </Typography>
        <Stack direction="row" mb={1} flexWrap="wrap" sx={{ gap: 1 }}>
          <Chip size="small" label={getContractorTypeLabel(contractor)} />
          <Chip size="small" label={activation.label} color={activation.color} />
          <Chip size="small" label={contractor.isActivePaidPlan ? 'Paid plan' : 'No paid plan'} color={contractor.isActivePaidPlan ? 'success' : 'default'} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Trial: {trial}
        </Typography>
      </CardContent>
    </Card>
  )
}
